import React from 'react';
import { useNavigate } from 'react-router-dom';
import { XIcon, CalendarIcon, DollarSignIcon, UsersIcon, ClockIcon, MapPinIcon } from 'lucide-react';
import { Button } from './Button';
type ProgramDetailModalProps = {
  program: any;
  isOpen: boolean;
  onClose: () => void;
};
export function ProgramDetailModal({
  program,
  isOpen,
  onClose
}: ProgramDetailModalProps) {
  const navigate = useNavigate();
  if (!isOpen) return null;
  const handleBookNow = () => {
    navigate('/booking', {
      state: {
        program
      }
    });
    onClose();
  };
  return <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-347">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0" data-id="element-348">
        <div className="fixed inset-0 transition-opacity" onClick={onClose} data-id="element-349">
          <div className="absolute inset-0 bg-black opacity-50" data-id="element-350"></div>
        </div>
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" data-id="element-351">
          &#8203;
        </span>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full" data-id="element-352">
          <div className="absolute top-0 right-0 pt-4 pr-4" data-id="element-353">
            <button type="button" className="text-gray-400 hover:text-gray-500 focus:outline-none" onClick={onClose} data-id="element-354">
              <XIcon className="h-6 w-6" data-id="element-355" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2" data-id="element-356">
            <div className="h-64 md:h-auto overflow-hidden" data-id="element-357">
              <img src={program.image} alt={program.title} className="w-full h-full object-cover" data-id="element-358" />
            </div>
            <div className="p-6 max-h-[80vh] overflow-y-auto" data-id="element-359">
              <div className="mb-4" data-id="element-360">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#0099A1]/10 text-[#0099A1]" data-id="element-361">
                  {program.category}
                </span>
              </div>
              <h3 className="text-2xl font-bold text-[#374047] mb-2" data-id="element-362">
                {program.title}
              </h3>
              <p className="text-[#374047]/80 mb-6" data-id="element-363">{program.description}</p>
              <div className="space-y-4 mb-6" data-id="element-364">
                <div className="flex items-center" data-id="element-365">
                  <CalendarIcon className="h-5 w-5 text-[#0099A1] mr-3" data-id="element-366" />
                  <div data-id="element-367">
                    <p className="text-sm font-medium text-[#374047]" data-id="element-368">
                      Duration
                    </p>
                    <p className="text-[#374047]/80" data-id="element-369">{program.duration}</p>
                  </div>
                </div>
                <div className="flex items-center" data-id="element-370">
                  <DollarSignIcon className="h-5 w-5 text-[#0099A1] mr-3" data-id="element-371" />
                  <div data-id="element-372">
                    <p className="text-sm font-medium text-[#374047]" data-id="element-373">Price</p>
                    <p className="text-[#374047]/80" data-id="element-374">{program.price}</p>
                  </div>
                </div>
                <div className="flex items-center" data-id="element-375">
                  <UsersIcon className="h-5 w-5 text-[#0099A1] mr-3" data-id="element-376" />
                  <div data-id="element-377">
                    <p className="text-sm font-medium text-[#374047]" data-id="element-378">
                      Group Size
                    </p>
                    <p className="text-[#374047]/80" data-id="element-379">{program.groupSize}</p>
                  </div>
                </div>
                <div className="flex items-center" data-id="element-380">
                  <ClockIcon className="h-5 w-5 text-[#0099A1] mr-3" data-id="element-381" />
                  <div data-id="element-382">
                    <p className="text-sm font-medium text-[#374047]" data-id="element-383">
                      Upcoming Dates
                    </p>
                    <p className="text-[#374047]/80" data-id="element-384">
                      {program.dates.join(', ')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center" data-id="element-385">
                  <MapPinIcon className="h-5 w-5 text-[#0099A1] mr-3" data-id="element-386" />
                  <div data-id="element-387">
                    <p className="text-sm font-medium text-[#374047]" data-id="element-388">
                      Location
                    </p>
                    <p className="text-[#374047]/80" data-id="element-389">{program.location}</p>
                  </div>
                </div>
              </div>
              <div className="mb-6" data-id="element-390">
                <h4 className="text-lg font-semibold text-[#374047] mb-2" data-id="element-391">
                  Highlights
                </h4>
                <ul className="space-y-2" data-id="element-392">
                  {program.highlights.map((highlight, index) => <li key={index} className="flex items-start" data-id="element-393">
                      <div className="h-5 w-5 text-[#0099A1] mr-3 flex-shrink-0" data-id="element-394">
                        •
                      </div>
                      <span className="text-[#374047]/80" data-id="element-395">{highlight}</span>
                    </li>)}
                </ul>
              </div>
              {program.hostImage && program.hostName && <div className="mb-6 bg-[#F4E8D0]/30 p-4 rounded-lg" data-id="element-396">
                  <h4 className="text-lg font-semibold text-[#374047] mb-3" data-id="element-397">
                    Your Host
                  </h4>
                  <div className="flex items-start" data-id="element-398">
                    <img src={program.hostImage} alt={program.hostName} className="w-20 h-20 rounded-full object-cover mr-4 flex-shrink-0" data-id="element-399" />
                    <div data-id="element-400">
                      <p className="font-semibold text-[#374047] mb-1" data-id="element-401">
                        {program.hostName}
                      </p>
                      {program.hostBio && <p className="text-sm text-[#374047]/80" data-id="element-402">
                          {program.hostBio}
                        </p>}
                    </div>
                  </div>
                </div>}
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-403">
                <Button variant="primary" onClick={handleBookNow} className="flex-1" data-id="element-404">
                  Book Now
                </Button>
                <Button variant="outline" onClick={onClose} className="flex-1" data-id="element-405">
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}